/**
 * Gospel Presentation Component
 *
 * Multi-step presentation of the Gospel message with navigation.
 * Accessible from the Prayer Request modal.
 */

import React, { useState } from 'react';
import './GospelPresentation.css';

interface GospelPresentationProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  userName?: string;
  userEmail?: string;
  portalSource: string;
}

type GospelStep =
  | 'made-for-more'
  | 'something-wrong'
  | 'what-god-did'
  | 'gift-cannot-earn'
  | 'your-response'
  | 'prayer'
  | 'questions'
  | 'find-church'
  | 'contact';

interface StepContent {
  title: string;
  body: string;
  verses?: { text: string; reference: string }[];
}

const GOSPEL_CONTENT: Record<string, StepContent> = {
  'made-for-more': {
    title: 'You Were Made for More',
    body: "God created you in His image - not by accident, but on purpose and with purpose. He designed you for a relationship with Him, to know His love and walk with Him.",
    verses: [
      {
        text: 'For God so loved the world that He gave His one and only Son, that whoever believes in Him shall not perish but have eternal life.',
        reference: 'John 3:16',
      },
    ],
  },
  'something-wrong': {
    title: 'Something Went Wrong',
    body: "But there's a problem we all share. Every one of us has sinned - we've turned our own way, missed the mark, and that sin separates us from a holy God. We can't fix this on our own, no matter how hard we try.",
    verses: [
      {
        text: 'For all have sinned and fall short of the glory of God.',
        reference: 'Romans 3:23',
      },
      {
        text: 'For the wages of sin is death, but the gift of God is eternal life in Christ Jesus our Lord.',
        reference: 'Romans 6:23',
      },
    ],
  },
  'what-god-did': {
    title: 'What God Did for You',
    body: "Here's the good news: God didn't leave us stranded. While we were still lost in our sin, He made a way. Jesus Christ - fully God and fully man - came to earth, lived a perfect life, and died on the cross to pay the penalty we deserved. Three days later, He rose from the grave, defeating sin and death forever.",
    verses: [
      {
        text: 'But God demonstrates His own love for us in this: While we were still sinners, Christ died for us.',
        reference: 'Romans 5:8',
      },
      {
        text: 'Jesus answered, "I am the way and the truth and the life. No one comes to the Father except through me."',
        reference: 'John 14:6',
      },
    ],
  },
  'gift-cannot-earn': {
    title: 'A Gift You Cannot Earn',
    body: "Salvation isn't something you can work for or earn - it's a free gift. God's grace reaches out to every person, drawing us to Himself. But it's a gift that must be received. Through faith - simply trusting in Jesus and what He did for you - you can be forgiven, made new, and brought into relationship with God.",
    verses: [
      {
        text: 'For it is by grace you have been saved, through faith - and this is not from yourselves, it is the gift of God - not by works, so that no one can boast.',
        reference: 'Ephesians 2:8-9',
      },
    ],
  },
  'your-response': {
    title: 'Your Response',
    body: "This isn't about religion or rituals. It's about a relationship. God is inviting you - right now - to turn from your old life, believe in Jesus, and receive forgiveness and new life in Him. He's faithful to forgive, and His Spirit will come to live in you, giving you the power to live for Him.",
    verses: [
      {
        text: 'If you declare with your mouth, "Jesus is Lord," and believe in your heart that God raised Him from the dead, you will be saved.',
        reference: 'Romans 10:9',
      },
      {
        text: 'Here I am! I stand at the door and knock. If anyone hears my voice and opens the door, I will come in and eat with that person, and they with me.',
        reference: 'Revelation 3:20',
      },
    ],
  },
};

const MAIN_STEPS: GospelStep[] = [
  'made-for-more',
  'something-wrong',
  'what-god-did',
  'gift-cannot-earn',
  'your-response',
];

export const GospelPresentation: React.FC<GospelPresentationProps> = ({
  isOpen,
  onClose,
  onBack,
  userName,
  userEmail,
  portalSource,
}) => {
  const [currentStep, setCurrentStep] = useState<GospelStep>('made-for-more');
  const [contactMessage, setContactMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContactSuccess, setShowContactSuccess] = useState(false);

  const currentIndex = MAIN_STEPS.indexOf(currentStep);
  const isMainStep = currentIndex !== -1;

  const handleNext = () => {
    if (currentIndex < MAIN_STEPS.length - 1) {
      setCurrentStep(MAIN_STEPS[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentStep(MAIN_STEPS[currentIndex - 1]);
    } else if (currentIndex === 0) {
      onBack();
    }
  };

  const handleContactSubmit = async () => {
    if (!contactMessage.trim()) return;

    setIsSubmitting(true);
    try {
      // Submit as a prayer request with context
      const endpoint = `/prayer-requests/`;
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          request_text: `[Gospel Inquiry] ${contactMessage}`,
          anonymous: false,
          include_name: true,
          allow_follow_up: true,
          customer_name: userName,
          customer_email: userEmail,
        }),
      });
      setShowContactSuccess(true);
    } catch (err) {
      console.error('Contact submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const renderMainStep = () => {
    const content = GOSPEL_CONTENT[currentStep];
    if (!content) return null;

    return (
      <>
        <div className="gospel-step-indicator">
          {MAIN_STEPS.map((step, i) => (
            <span
              key={step}
              className={`gospel-step-dot ${i === currentIndex ? 'active' : ''} ${i < currentIndex ? 'completed' : ''}`}
            />
          ))}
        </div>

        <h2 className="gospel-title">{content.title}</h2>
        <p className="gospel-body">{content.body}</p>

        {content.verses && (
          <div className="gospel-verses">
            {content.verses.map((verse, i) => (
              <blockquote key={i} className="gospel-verse">
                <p>"{verse.text}"</p>
                <cite>— {verse.reference}</cite>
              </blockquote>
            ))}
          </div>
        )}

        <div className="gospel-navigation">
          <button className="gospel-btn gospel-btn-secondary" onClick={handlePrev}>
            &larr; Back
          </button>

          {currentStep === 'your-response' ? (
            <div className="gospel-response-buttons">
              <button
                className="gospel-btn gospel-btn-primary"
                onClick={() => setCurrentStep('prayer')}
              >
                I'd Like to Pray
              </button>
              <button
                className="gospel-btn gospel-btn-outline"
                onClick={() => setCurrentStep('questions')}
              >
                I Have Questions
              </button>
            </div>
          ) : (
            <button className="gospel-btn gospel-btn-primary" onClick={handleNext}>
              Next &rarr;
            </button>
          )}
        </div>
      </>
    );
  };

  const renderPrayer = () => (
    <>
      <h2 className="gospel-title">A Simple Prayer</h2>
      <p className="gospel-body">
        There's no magic formula - God knows your heart. But if you're ready,
        you can pray something like this:
      </p>

      <div className="gospel-prayer-box">
        <p>
          Lord Jesus, I know I'm a sinner and I can't save myself. I believe You
          died for me and rose again. I turn from my sin and put my trust in You.
          Forgive me, come into my life, and make me new. I receive You as my Lord
          and Savior. Amen.
        </p>
      </div>

      <p className="gospel-body">
        If you prayed that and meant it, you've just begun the most important
        journey of your life. We'd love to hear from you and help you take the
        next steps.
      </p>

      <div className="gospel-navigation gospel-navigation-stacked">
        <button
          className="gospel-btn gospel-btn-primary"
          onClick={() => setCurrentStep('contact')}
        >
          Let Us Know
        </button>
        <button
          className="gospel-btn gospel-btn-outline"
          onClick={() => setCurrentStep('find-church')}
        >
          Find a Church Near Me
        </button>
        <button className="gospel-btn gospel-btn-secondary" onClick={() => setCurrentStep('your-response')}>
          &larr; Back
        </button>
      </div>
    </>
  );

  const renderQuestions = () => (
    <>
      <h2 className="gospel-title">We're Here to Help</h2>
      <p className="gospel-body">
        Questions are welcome - faith isn't about blind leaps, and God invites
        honest seeking. If you'd like to talk with someone about what you've read,
        we'd be honored to connect with you.
      </p>

      <div className="gospel-navigation gospel-navigation-stacked">
        <button
          className="gospel-btn gospel-btn-primary"
          onClick={() => setCurrentStep('contact')}
        >
          Send Us a Message
        </button>
        <button className="gospel-btn gospel-btn-secondary" onClick={() => setCurrentStep('your-response')}>
          &larr; Back
        </button>
      </div>
    </>
  );

  const renderFindChurch = () => (
    <>
      <h2 className="gospel-title">Find a Church</h2>
      <p className="gospel-body">
        We'd encourage you to find a Bible-believing church where you can grow in
        your faith and be part of a community. Here are two church finders we trust:
      </p>

      <div className="gospel-church-links">
        <a
          href="https://findachurch.nazarene.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="gospel-church-link"
        >
          <div className="gospel-church-name">Church of the Nazarene</div>
          <div className="gospel-church-url">findachurch.nazarene.org</div>
        </a>
        <a
          href="https://ag.org/Resources/Directories/Church-Directory?S=WA"
          target="_blank"
          rel="noopener noreferrer"
          className="gospel-church-link"
        >
          <div className="gospel-church-name">Assemblies of God</div>
          <div className="gospel-church-url">ag.org/Resources/Directories</div>
        </a>
      </div>

      <p className="gospel-body-small">
        If you're in the Inland Northwest and want a personal recommendation,
        reach out - we'd be happy to connect you with a church we know.
      </p>

      <div className="gospel-navigation gospel-navigation-stacked">
        <button
          className="gospel-btn gospel-btn-outline"
          onClick={() => setCurrentStep('contact')}
        >
          Contact Us
        </button>
        <button className="gospel-btn gospel-btn-secondary" onClick={() => setCurrentStep('prayer')}>
          &larr; Back
        </button>
      </div>
    </>
  );

  const renderContact = () => {
    if (showContactSuccess) {
      return (
        <>
          <div className="gospel-success-icon">
            <span className="gospel-cross-large">✝</span>
          </div>
          <h2 className="gospel-title">Message Sent</h2>
          <p className="gospel-body">
            Thank you for reaching out. We'll be in touch soon. May God bless you
            on your journey.
          </p>
          <button className="gospel-btn gospel-btn-primary" onClick={onClose}>
            Close
          </button>
        </>
      );
    }

    return (
      <>
        <h2 className="gospel-title">Send Us a Message</h2>
        <p className="gospel-body">
          Share what's on your heart, any questions you have, or let us know
          about a decision you've made. We'd be honored to hear from you.
        </p>

        <div className="gospel-form-group">
          <textarea
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            placeholder="Write your message here..."
            rows={5}
            className="gospel-textarea"
            disabled={isSubmitting}
          />
        </div>

        <div className="gospel-navigation gospel-navigation-stacked">
          <button
            className="gospel-btn gospel-btn-primary"
            onClick={handleContactSubmit}
            disabled={isSubmitting || !contactMessage.trim()}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          <button
            className="gospel-btn gospel-btn-secondary"
            onClick={() => {
              if (currentStep === 'contact') {
                setCurrentStep('your-response');
              }
            }}
          >
            &larr; Back
          </button>
        </div>
      </>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'prayer':
        return renderPrayer();
      case 'questions':
        return renderQuestions();
      case 'find-church':
        return renderFindChurch();
      case 'contact':
        return renderContact();
      default:
        return renderMainStep();
    }
  };

  return (
    <div className="gospel-overlay" onClick={onClose}>
      <div className="gospel-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gospel-close" onClick={onClose}>
          &times;
        </button>

        <div className="gospel-content">{renderStep()}</div>
      </div>
    </div>
  );
};

export default GospelPresentation;
